import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowRightLeft, List, Plus, Search, TableProperties } from 'lucide-react';
import { Checkbox } from '../ui/checkbox';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import LoadingRing from '../ui-components/LoadingRing';
import ActorTable from './ActorTable';
import ActorMatrix from './ActorMatrix';
import { getActorDisplayName } from '../../utils/roleUtils';
import ActorFormDrawer from './ActorFormDrawer';
import MigrateActorUsersDialog from './MigrateActorUsersDialog';
import ConfirmActionDialog from '../department-policies/ConfirmActionDialog';
import {
    getActors,
    createActor,
    updateActor,
    deleteActor,
    type ActorWithCount,
    type ActorScope,
} from '../../services/api/actorService';
import { useToast } from '../../hooks/use-toast';

type SortOption = 'name' | 'users-desc' | 'users-asc';
type ViewMode = 'list' | 'matrix';

interface ManageActorsTabProps {
    scopeRestriction?: 'department';
}

export default function ManageActorsTab({ scopeRestriction }: ManageActorsTabProps) {
    const limited = !!scopeRestriction;
    const { toast } = useToast();
    const [actors, setActors] = useState<ActorWithCount[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState<SortOption>('name');
    const [viewMode, setViewMode] = useState<ViewMode>('matrix');
    const [activeDeptsOnly, setActiveDeptsOnly] = useState(true);

    const [formOpen, setFormOpen] = useState(false);
    const [editingActor, setEditingActor] = useState<ActorWithCount | null>(
        null
    );
    const [deleteTarget, setDeleteTarget] = useState<ActorWithCount | null>(
        null
    );
    const [migrateOpen, setMigrateOpen] = useState(false);

    const fetchActors = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getActors(activeDeptsOnly, scopeRestriction);
            setActors(data);
        } catch {
            toast({ title: 'Failed to load actors', variant: 'destructive' });
        } finally {
            setLoading(false);
        }
    }, [toast, activeDeptsOnly, scopeRestriction]);

    useEffect(() => {
        fetchActors();
    }, [fetchActors]);

    const filteredActors = useMemo(() => {
        let result = actors;
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            result = result.filter((a) =>
                a.role.toLowerCase().includes(term)
            );
        }
        result = [...result].sort((a, b) => {
            switch (sortBy) {
                case 'users-desc':
                    return b.userCount - a.userCount;
                case 'users-asc':
                    return a.userCount - b.userCount;
                default:
                    return a.role.localeCompare(b.role);
            }
        });
        return result;
    }, [actors, searchTerm, sortBy]);

    const handleCreate = () => {
        setEditingActor(null);
        setFormOpen(true);
    };

    const handleEdit = (actor: ActorWithCount) => {
        setEditingActor(actor);
        setFormOpen(true);
    };

    const handleSave = async (data: {
        role: string;
        description: string;
        scope?: ActorScope;
        rights: string[];
        deptPermissions: string[];
        alternativeNames: Record<string, string>;
        alternativeDescriptions: Record<string, string>;
    }) => {
        if (editingActor) {
            // Role names are fixed after creation — never sent on update.
            const { role: _role, ...updates } = data;
            await updateActor(
                editingActor._id,
                limited
                    ? {
                          description: updates.description,
                          deptPermissions: updates.deptPermissions,
                          alternativeNames: updates.alternativeNames,
                          alternativeDescriptions: updates.alternativeDescriptions,
                      }
                    : updates
            );
            toast({ title: `Actor "${getActorDisplayName(data)}" updated` });
        } else {
            await createActor(data);
            toast({ title: `Actor "${getActorDisplayName(data)}" created` });
        }
        await fetchActors();
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            await deleteActor(deleteTarget._id);
            toast({ title: `Actor "${getActorDisplayName(deleteTarget)}" deleted` });
            await fetchActors();
        } catch {
            toast({
                title: 'Cannot delete actor with assigned users',
                variant: 'destructive',
            });
        }
        setDeleteTarget(null);
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-1 items-center gap-2">
                    <div className="relative flex-1 sm:max-w-xs">
                        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search actors…"
                            className="pl-9"
                        />
                    </div>
                    <Select
                        value={sortBy}
                        onValueChange={(v) => setSortBy(v as SortOption)}
                    >
                        <SelectTrigger className="h-9 w-[160px]">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="name">Name A–Z</SelectItem>
                            <SelectItem value="users-desc">
                                Most users
                            </SelectItem>
                            <SelectItem value="users-asc">
                                Fewest users
                            </SelectItem>
                        </SelectContent>
                    </Select>
                    <div className="flex rounded-lg border border-input bg-background p-0.5 shadow-sm">
                        <button
                            type="button"
                            onClick={() => setViewMode('matrix')}
                            className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                                viewMode === 'matrix'
                                    ? 'bg-accent text-accent-foreground'
                                    : 'text-muted-foreground hover:text-foreground'
                            }`}
                        >
                            <TableProperties className="h-3.5 w-3.5" />
                            <span className="hidden sm:inline">Matrix</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => setViewMode('list')}
                            className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                                viewMode === 'list'
                                    ? 'bg-accent text-accent-foreground'
                                    : 'text-muted-foreground hover:text-foreground'
                            }`}
                        >
                            <List className="h-3.5 w-3.5" />
                            <span className="hidden sm:inline">List</span>
                        </button>
                    </div>
                    <label className="flex items-center gap-1.5 text-xs text-muted-foreground whitespace-nowrap">
                        <Checkbox
                            checked={activeDeptsOnly}
                            onCheckedChange={(v) => setActiveDeptsOnly(!!v)}
                        />
                        Active depts only
                    </label>
                </div>
                {!limited && (
                    <div className="flex flex-col gap-2 sm:flex-row">
                        <Button
                            variant="outline"
                            onClick={() => setMigrateOpen(true)}
                            className="w-full sm:w-auto"
                        >
                            <ArrowRightLeft className="mr-2 h-4 w-4" />
                            Migrate Users
                        </Button>
                        <Button onClick={handleCreate} className="w-full sm:w-auto">
                            <Plus className="mr-2 h-4 w-4" />
                            Create Actor
                        </Button>
                    </div>
                )}
            </div>

            {loading ? (
                <div className="flex justify-center py-16">
                    <LoadingRing size="large" />
                </div>
            ) : viewMode === 'list' ? (
                <ActorTable
                    actors={filteredActors}
                    activeDeptsOnly={activeDeptsOnly}
                    limited={limited}
                    onEdit={handleEdit}
                    onDelete={setDeleteTarget}
                />
            ) : (
                <ActorMatrix
                    actors={filteredActors}
                    limited={limited}
                    onActorsChange={setActors}
                    onEdit={handleEdit}
                />
            )}

            <ActorFormDrawer
                open={formOpen}
                actor={editingActor}
                limited={limited}
                onOpenChange={setFormOpen}
                onSave={handleSave}
            />

            <MigrateActorUsersDialog
                open={migrateOpen}
                actors={actors}
                onOpenChange={setMigrateOpen}
                onMigrated={fetchActors}
            />

            <ConfirmActionDialog
                open={!!deleteTarget}
                onOpenChange={(open) => !open && setDeleteTarget(null)}
                title="Delete Actor"
                description={`Are you sure you want to delete "${deleteTarget ? getActorDisplayName(deleteTarget) : ''}"? This action cannot be undone.`}
                confirmLabel="Delete"
                onConfirm={handleDelete}
            />
        </div>
    );
}
